import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';

interface PageLoadStatusContextType {
  registerComponent: (componentName: string) => void;
  markComponentLoaded: (componentName: string) => void;
  markComponentError: (componentName: string, error?: Error) => void;
  resetPageLoadStatus: () => void;
  isPageFullyLoaded: boolean;
  loadedComponents: string[];
  expectedComponents: string[];
  hasError: boolean;
  errorDetails: Array<{ component: string; error?: Error }>;
}

const PageLoadStatusContext = createContext<
  PageLoadStatusContextType | undefined
>(undefined);

interface PageLoadStatusProviderProps {
  children: ReactNode;
}

export function PageLoadStatusProvider({
  children,
}: PageLoadStatusProviderProps) {
  const [loadedComponents, setLoadedComponents] = useState<Set<string>>(
    new Set()
  );
  const [expectedComponents, setExpectedComponents] = useState<Set<string>>(
    new Set()
  );
  const [errorDetails, setErrorDetails] = useState<
    Array<{ component: string; error?: Error }>
  >([]);

  const location = useLocation();
  const [currentPath, setCurrentPath] = useState<string>(location.pathname);
  const hasPageLoadMonitoringFeature = useUserHasFeature(
    Feature.PageLoadMonitoring
  );

  const registerComponent = useCallback(
    (componentName: string) => {
      if (!hasPageLoadMonitoringFeature) return;

      if (!componentName || typeof componentName !== 'string') {
        console.warn('[PageLoadStatus] Invalid component name:', componentName);
        return;
      }

      setExpectedComponents((prev) => {
        if (prev.has(componentName)) {
          console.warn(
            '[PageLoadStatus] Component already registered:',
            componentName
          );
          return prev;
        }
        return new Set(prev).add(componentName);
      });
    },
    [hasPageLoadMonitoringFeature]
  );

  const markComponentLoaded = useCallback(
    (componentName: string) => {
      if (!hasPageLoadMonitoringFeature) return;

      setLoadedComponents((prev) => new Set(prev).add(componentName));
    },
    [hasPageLoadMonitoringFeature]
  );

  const markComponentError = useCallback(
    (componentName: string, error?: Error) => {
      if (!hasPageLoadMonitoringFeature) return;
      setErrorDetails((prev) => [...prev, { component: componentName, error }]);
    },
    [hasPageLoadMonitoringFeature]
  );

  const hasError = errorDetails.length > 0;
  const isPageFullyLoaded = useMemo(() => {
    return (
      expectedComponents.size > 0 &&
      loadedComponents.size === expectedComponents.size &&
      !hasError
    );
  }, [expectedComponents, loadedComponents, hasError]);

  // Global error handlers
  useEffect(() => {
    if (!hasPageLoadMonitoringFeature) return;

    const handleError = (event: ErrorEvent) => {
      markComponentError('window', new Error(event.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      markComponentError('window', new Error(String(event.reason)));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [hasPageLoadMonitoringFeature, markComponentError]);

  const resetPageLoadStatus = useCallback(() => {
    if (currentPath === location.pathname) return;

    setCurrentPath(location.pathname);
    setLoadedComponents(new Set());
    setExpectedComponents(new Set());
    setErrorDetails([]);
  }, [currentPath, location.pathname]);

  // Automatically reset when navigation occurs
  useEffect(() => {
    if (!hasPageLoadMonitoringFeature) return;

    const timeoutId = setTimeout(() => {
      resetPageLoadStatus();
    }, 0); // Schedule after current execution stack

    return () => clearTimeout(timeoutId);
  }, [location.pathname, resetPageLoadStatus, hasPageLoadMonitoringFeature]);

  // Add timeout protection - if components don't load within 30 seconds, mark as error
  useEffect(() => {
    if (!hasPageLoadMonitoringFeature) return;

    const timeoutId = setTimeout(() => {
      if (expectedComponents.size > 0 && !isPageFullyLoaded && !hasError) {
        const unloadedComponents = Array.from(expectedComponents).filter(
          (comp) => !loadedComponents.has(comp)
        );
        markComponentError(
          'timeout',
          new Error(
            `Page load timeout exceeded. Unloaded components: ${unloadedComponents.join(', ')}`
          )
        );
      }
    }, 30000); // 30 second timeout

    return () => clearTimeout(timeoutId);
  }, [
    expectedComponents.size,
    isPageFullyLoaded,
    hasError,
    expectedComponents,
    loadedComponents,
    markComponentError,
    hasPageLoadMonitoringFeature,
  ]);

  // Debug logging in development mode
  const isDebugMode = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (
      !hasPageLoadMonitoringFeature ||
      !isDebugMode ||
      expectedComponents.size === 0
    )
      return;

    if (!isPageFullyLoaded && expectedComponents.size > 0) {
      const unloadedComponents = Array.from(expectedComponents).filter(
        (comp) => !loadedComponents.has(comp)
      );
      console.warn('Unloaded components:', unloadedComponents);
    }
    console.groupEnd();
  }, [
    expectedComponents,
    loadedComponents,
    errorDetails,
    isPageFullyLoaded,
    hasError,
    isDebugMode,
    hasPageLoadMonitoringFeature,
  ]);

  const contextValue = useMemo(
    (): PageLoadStatusContextType => ({
      registerComponent,
      markComponentLoaded,
      markComponentError,
      resetPageLoadStatus,
      isPageFullyLoaded,
      loadedComponents: Array.from(loadedComponents),
      expectedComponents: Array.from(expectedComponents),
      hasError,
      errorDetails,
    }),
    [
      registerComponent,
      markComponentLoaded,
      markComponentError,
      resetPageLoadStatus,
      isPageFullyLoaded,
      loadedComponents,
      expectedComponents,
      hasError,
      errorDetails,
    ]
  );

  return (
    <PageLoadStatusContext.Provider value={contextValue}>
      {children}
    </PageLoadStatusContext.Provider>
  );
}

export function usePageLoadStatus(): PageLoadStatusContextType {
  const context = useContext(PageLoadStatusContext);
  if (context === undefined) {
    throw new Error(
      'usePageLoadStatus must be used within a PageLoadStatusProvider'
    );
  }
  return context;
}
